import { Chat, LoadingAnimation, Prompt } from '../Types';
import {
    ContentStreamMessage,
    ContentStreamState,
    CostUpdateMessage,
    SocketMessageTypes,
    TitleListUpdateMessage,
    TokenCountUpdateMessage,
} from '../Types/SocketMessage';
import { DLPState } from '../Types/Enums';
import { RootState } from '../Store/Reducers';
import {
    addMessage,
    setChatMessages,
    setChat as setGlobalChat,
    setIsStopable,
    setMessagesLoaded,
    updateMessage,
} from '../Store/Actions/chatActions';
import { setChatLoaded, setIsNewChat, setIsSending, setUpdateChat } from '../Store/Actions/chatActions';
import { setSelectedPrompt as updatePrompt } from '../Store/Actions/environmentActions';
import {
    setNavigationImage,
    setNavigationSubTitle,
    setNavigationTitle,
} from '../Store/Actions/navigationHeaderActions';
import { SocketContext } from '../SocketContext';
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useGlobals from './useGlobals';
import useResizableContainer from './useResizableContainer';
import { chatService } from '../Services/chatService';
import { setMessageSource } from '../Store/Actions/messageSourceActions';

type ChatProps = {
    chatId?: string;
    promptPreview?: boolean;
    assistantIdentifier?: string;
    initMessage?: string;
};

const useChat = ({
    chatId: propChatId,
    assistantIdentifier: propAssistantIdentifier,
    initMessage: propInitMessage,
}: ChatProps) => {
    const { userSettings, i18n, demo, windowWidth, currentURL, dispatch, getBreakpoint } = useGlobals();

    const { chatId: paramChatId } = useParams<{ chatId: string }>();
    const chatId = useMemo(() => {
        const result = propChatId || (paramChatId as string);
        return result;
    }, [propChatId, paramChatId]);

    const { assistantIdentifier: paramAssistantIdentifier, initMessage: paramInitMessage } = useParams<{
        assistantIdentifier: string;
        initMessage: string;
    }>() as { assistantIdentifier: string; initMessage: string };
    const assistantIdentifier = propAssistantIdentifier || paramAssistantIdentifier;
    const initMessage =
        propInitMessage || paramInitMessage
            ? decodeURI((propInitMessage || paramInitMessage).replace(/\+/g, '%20'))
            : undefined;
    const { socket, addMessageHandler } = useContext(SocketContext);

    const [keyboardVisible, setKeyboardVisible] = useState<boolean>(false);
    const [showSplitButton, setShowSplitButton] = useState<boolean>(false);
    const [chatWidth, setChatWidth] = useState<number>(windowWidth);

    const isDragActive = useSelector((state: RootState) => state.environment.isDragActive);
    const dragZoneRootProps = useSelector((state: RootState) => state.environment.dragZoneRootProps);
    const dragZoneInputProps = useSelector((state: RootState) => state.environment.dragZoneInputProps);

    const chatLoaded = useSelector((state: RootState) => state.chats.chatLoaded);
    const updateChat = useSelector((state: RootState) => state.chats.updateChat);
    const websocketError = useSelector((state: RootState) => state.chats.websocketError);
    const chatlistOpen = useSelector((state: RootState) => state.chatlist.chatlistOpen);
    const messageSourceIdentifier = useSelector((state: RootState) => state.messageSource.messageIdentifier);

    const basePrompt = useSelector((state: RootState) => state.environment.basePrompt as Prompt);
    const prompts = useSelector((state: RootState) => state.chats.prompts as Prompt[]);
    const userPrompts = useSelector((state: any) => state.environment.userPrompts);
    const selectedPromptIdentifier = useSelector((state: RootState) =>
        state.environment.selectedPrompt !== ''
            ? state.environment.selectedPrompt
            : basePrompt.identifier ?? prompts.find((prompt: Prompt) => prompt.isBasePrompt)?.identifier,
    ) as string;
    const [showInput, setShowInput] = useState<boolean>(true);

    const [selectedPrompt, setSelectedPrompt] = useState<Prompt | undefined>(
        prompts.find((prompt: Prompt) => prompt.identifier === selectedPromptIdentifier),
    );

    const chat = useSelector((state: RootState) => state.chats.chat as Chat);
    const [dlpState, setDlpState] = useState<DLPState>(DLPState.dlp_optional);
    const chatMessages = useSelector((state: RootState) => state.chats.chatMessages);

    const [loadingAnimation, setLoadingAnimation] = useState<LoadingAnimation>({
        state: 'closed',
        content: '',
        percentage: 100,
        icon: 'Check',
    });

    const chatContainerStyles = useSelector((state: RootState) => state.chats.chatContainerStyles);

    const { resizeContainerRef, handleMouseDown } = useResizableContainer({
        chatId,
        chatWidth,
    });

    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const resizerRef = useRef<HTMLDivElement | null>(null);
    const isFirstScroll = useRef<boolean>(true);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const promptPreview = !currentURL.includes('chat') && !currentURL.includes('start') && !currentURL.endsWith('/');

    const [chatIdentifierLocal, setChatIdentifierLocal] = useState<string | undefined>(undefined);

    const updateChatDetails = useCallback(
        (updates: Partial<Chat>) => {
            dispatch(setGlobalChat({ ...updates }));
        },
        [dispatch],
    );

    useEffect(() => {
        if (
            assistantIdentifier !== '' &&
            prompts.find((prompt) => prompt.identifier === assistantIdentifier) !== undefined
        ) {
            dispatch(updatePrompt(assistantIdentifier));
        }
    }, [prompts, assistantIdentifier, dispatch]);

    useEffect(() => {
        setShowInput(
            userPrompts.find((prompt: Prompt) => prompt.identifier === selectedPromptIdentifier) !== undefined,
        );
    }, [userPrompts, selectedPromptIdentifier]);

    const chatloadingRef = useRef(false);

    useEffect(() => {
        if (chatId !== undefined) {
            if (chatloadingRef.current) return;

            chatloadingRef.current = true;
            dispatch(setMessagesLoaded(false));
            const fetchChat = async () => {
                await chatService.getChat(chatId).then((response) => {
                    dispatch(setGlobalChat(response.data));
                    let tempChats: Record<string, any> = {};
                    response.data.messages.forEach((message: any) => {
                        tempChats[message.identifier] = {
                            identifier: message.identifier,
                            role: message.role,
                            content: message.content,
                            images: message.images,
                            files: message.files,
                            sources: message.sources || [],
                            contentChanged: message.contentChanged,
                            originalContent: message.originalContent,
                        };
                    });

                    dispatch(setChatMessages(tempChats));
                    if (!promptPreview) {
                        if (response.data.title !== '') dispatch(setNavigationTitle(response.data.title));
                        dispatch(setNavigationSubTitle(response.data.prompt_name));
                        dispatch(setNavigationImage(response.data.prompt_image));
                    }
                    dispatch(updatePrompt(response.data.prompt));
                    setChatIdentifierLocal(response.data.identifier);
                    chatloadingRef.current = false;
                    dispatch(setChatLoaded(true));
                    dispatch(setMessagesLoaded(true));
                });
            };

            fetchChat();
            dispatch(setIsNewChat(false));
        } else {
            chatloadingRef.current = false;
            updateChatDetails({ identifier: '', title: '', messages: [] });
            dispatch(setChatLoaded(false));
        }
        isFirstScroll.current = true;
        return () => {
            dispatch(
                setMessageSource({
                    messageIdentifier: '',
                    content: [{ title: '', description: '', url: '' }],
                }),
            );
        };
    }, [chatId, dispatch, updateChatDetails, promptPreview]);

    useEffect(() => {
        if (selectedPrompt && chatId === undefined) {
            updateChatDetails({ prompt_examples: selectedPrompt.examples });
            setDlpState(selectedPrompt.combinedDlp);
        }
    }, [selectedPrompt, chatId, updateChatDetails]);

    useEffect(() => {
        chatloadingRef.current = false;
    }, [chatId]);

    useEffect(() => {
        if (chatIdentifierLocal !== undefined) {
            dispatch(setChatLoaded(true));
        } else {
            dispatch(setChatLoaded(false));
        }
    }, [chatIdentifierLocal, dispatch]);

    useEffect(() => {
        setSelectedPrompt(prompts.find((prompt: Prompt) => prompt.identifier === selectedPromptIdentifier));
    }, [selectedPromptIdentifier, prompts]);

    useEffect(() => {
        if (chatId === undefined) return;

        const initialViewportHeight = window.visualViewport?.height ?? 0;
        const handleResize = () => {
            if (window.visualViewport && window.visualViewport.height < initialViewportHeight) {
                setKeyboardVisible(true);
            } else {
                setKeyboardVisible(false);
            }
        };
        window.visualViewport?.addEventListener('resize', handleResize);
        return () => {
            window.visualViewport?.removeEventListener('resize', handleResize);
        };
    }, [chatId]);

    useEffect(() => {
        if (chatId === undefined) return;
        let chatWidth;

        if (chatlistOpen && windowWidth > getBreakpoint('lg')) {
            if (messageSourceIdentifier !== '') {
                chatWidth = windowWidth - 700;
            } else {
                chatWidth = windowWidth - 300;
            }
        } else {
            if (messageSourceIdentifier !== '') {
                chatWidth = windowWidth;
            } else {
                chatWidth = windowWidth;
            }
        }

        setChatWidth(chatWidth);
    }, [chatlistOpen, windowWidth, chatId, getBreakpoint, messageSourceIdentifier]);

    const chatMessagesRef = useRef(chat.messages);

    useEffect(() => {
        chatMessagesRef.current = chat.messages;
    }, [chat.messages]);

    useEffect(() => {
        addMessageHandler(SocketMessageTypes.titleUpdate, (data: TitleListUpdateMessage) => {
            if (data.chatIdentifier === chatId) {
                updateChatDetails({ title: data.title });
                window.location.pathname.includes('chat') && dispatch(setNavigationTitle(data.title));
            }
        });

        addMessageHandler(SocketMessageTypes.tokenCountUpdate, (data: TokenCountUpdateMessage) => {
            if (data.chatIdentifier === chatId) {
                // setTokenCount(data.count);
            }
        });

        addMessageHandler(SocketMessageTypes.costUpdate, (data: CostUpdateMessage) => {
            if (data.chatIdentifier === chatId) {
                // setCosts(data.value);
            }
        });

        addMessageHandler('addSourcesAndImages', (data) => {
            if (Object.keys(chatMessages).includes(data.messageIdentifier) && data.messageIdentifier !== undefined) {
                dispatch(
                    updateMessage({
                        ...chatMessages[data.messageIdentifier],
                        sources: data.sources,
                        images: data.images,
                    }),
                );
            }
        });

        addMessageHandler(SocketMessageTypes.contentStream, (data: ContentStreamMessage) => {
            if (data.chatIdentifier === chatId || chatId === undefined) {
                switch (data.state) {
                    case ContentStreamState.complete:
                        dispatch(
                            addMessage({
                                identifier: data.messageIdentifier,
                                role: data.role,
                                content: data.content,
                                images: data.images,
                                files: data.files,
                                sources: data.sources || [],
                                contentChanged: data.contentChanged,
                                originalContent: data.originalContent,
                            }),
                        );
                        break;
                    case ContentStreamState.started:
                        dispatch(setIsSending(true));
                        dispatch(setIsStopable(true));
                        dispatch(
                            addMessage({
                                identifier: data.messageIdentifier,
                                role: data.role,
                                content: '',
                                images: data.images,
                                files: data.files,
                                sources: data.sources || [],
                                contentChanged: data.contentChanged,
                                originalContent: data.originalContent,
                            }),
                        );
                        setLoadingAnimation({
                            state: 'update',
                            content: i18n.translate({ id: 'openaiassistant.working', sourceName: 'LoadingAnimation' }),
                            percentage: 0,
                            icon: 'Spinner',
                        });

                        break;
                    case ContentStreamState.finished:
                        dispatch(setIsSending(false));
                        dispatch(setIsStopable(false));
                        break;
                    default:
                        if (data.messageIdentifier && chatMessages.hasOwnProperty(data.messageIdentifier)) {
                            dispatch(setIsSending(true));
                            dispatch(setIsStopable(true));

                            const messagePayload = {
                                identifier: data.messageIdentifier,
                                content: data.content,
                                contentChanged: data.contentChanged,
                                ...(data.images && { images: data.images }),
                            };

                            dispatch(updateMessage(messagePayload));
                        }
                        break;
                }
            }
        });

        addMessageHandler(SocketMessageTypes.loadingAnimation, (data: ContentStreamMessage) => {
            if (data.chatIdentifier === chatId) {
                if (data.state === 'closed') {
                    data.content = '';
                    data.value = 100;
                    data.icon = 'Check';
                    data.image = '';
                }

                setLoadingAnimation((prev) => {
                    if (
                        prev.state === data.state &&
                        prev.content === data.content &&
                        prev.percentage === (data.value ?? 100) &&
                        prev.icon === (data.icon ?? 'Check') &&
                        prev.image === (data.image ?? '')
                    ) {
                        return prev;
                    }
                    return {
                        ...prev,
                        state: data.state ?? 'closed',
                        content: data.content ?? '',
                        percentage: data.value ?? 100,
                        icon: data.icon ?? 'Check',
                        image: data.image ?? '',
                    };
                });
            }
        });
    }, [chatId, dispatch, updateChatDetails, addMessageHandler, chatMessages, chatLoaded, i18n]);

    const handleMouseMove = useCallback(() => {
        if (chatId === undefined) return;

        setShowSplitButton(true);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            setShowSplitButton(false);
        }, 1000);
    }, [chatId]);

    useEffect(() => {
        if (chatId === undefined) return;

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [chatId, showSplitButton, setShowSplitButton, handleMouseMove]);

    const sliceMessages = useCallback(
        (messageIdentifier: string) => {
            if (!chatId) return;
            const messagesArray = Object.values(chatMessages);
            const index = messagesArray.findIndex((message: any) => message.identifier === messageIdentifier);

            if (index === -1) return;

            const newMessages = messagesArray.slice(0, index);
            const newMessagesObject = Object.fromEntries(newMessages.map((msg) => [msg.identifier, msg]));

            dispatch(setChatMessages(newMessagesObject));
        },
        [chatId, chatMessages, dispatch],
    );

    useEffect(() => {
        if (chatId === undefined) return;
        if (userSettings.isLoggedIn && chatId !== undefined && !promptPreview && updateChat) {
            const data = new URLSearchParams();
            data.append('dlpActive', chat.dlp_active ? 'true' : 'false');
            chatService.updateDLP(chatId, data).then((response) => {
                dispatch(setUpdateChat(false));
                updateChatDetails({ dlp_active: response.data.dlp_active });
            });
        }
    }, [
        updateChat,
        chatId,
        userSettings.isLoggedIn,
        chat.dlp_active,
        dlpState,
        promptPreview,
        dispatch,
        updateChatDetails,
        chat.dlp,
    ]);

    // const ChatActions = useMemo(() => ({}), []);

    return {
        chat,
        i18n,
        dlpState,
        sliceMessages,
        socket,
        messagesEndRef,
        loadingAnimation,
        promptPreview,
        chatContainerStyles,
        websocketError,
        chatId,
        keyboardVisible,
        dragZoneRootProps,
        dragZoneInputProps,
        demo,
        showSplitButton,
        resizerRef,
        isDragActive,
        chatLoaded,
        handleMouseDown,
        resizeContainerRef,
        chatMessages,
        initMessage,
        showInput,
    };
};

export default useChat;
